import { Injectable, NgZone } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
    PushNotifications,
    PushNotificationSchema,
    ActionPerformed,
    Token
} from '@capacitor/push-notifications';
import { LocalNotifications } from '@capacitor/local-notifications';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { global_user } from '../global/global';
import { Subject } from 'rxjs';
import { TokenStorageService } from '../login/token-storage.service';
import { unreadVisibleCount } from '../utils/notification-access';
import { UserDataService } from '../user/user.service';
import { NetworkErrorService } from './network-error.service';
import { AppIdentityService } from './app-identity.service';

/*
  ╔══════════════════════════════════════════════════════════════════════╗
  ║              PUSH NOTIFICATION SERVICE - MİMARİ NOTU               ║
  ╠══════════════════════════════════════════════════════════════════════╣
  ║                                                                    ║
  ║  Strateji: Mobilde Polling Kapalı / Push-Driven Mimari             ║
  ║                                                                    ║
  ║  Web (Tarayıcı):                                                   ║
  ║    - Polling AÇIK (table-management her X saniyede backend'e sorar) ║
  ║    - Push Notification YOK (bu servis web'de çalışmaz)             ║
  ║                                                                    ║
  ║  Mobil (Capacitor/Android):                                        ║
  ║    - Polling KAPALI (table-management.component.ts bkz.)           ║
  ║    - Push Notification AÇIK (bu servis FCM dinler)                  ║
  ║    - Push geldiğinde:                                              ║
  ║      1. Ses çalar + Local Notification gösterir (foreground)       ║
  ║      2. pushReceived sinyali atar → TableManagement sessizce       ║
  ║         backend'den veri çeker (ses/popup YAPMAZ, sadece günceller)║
  ║      3. Bildirim listesine (çan ikonu) geçici obje ekler           ║
  ║                                                                    ║
  ╚══════════════════════════════════════════════════════════════════════╝
*/

@Injectable({ providedIn: 'root' })
export class PushNotificationService {

    private notificationAudio = new Audio('assets/sounds/notification.mp3');
    private cachedToken: string | null = null;

    // Dinleyen sayfalara (TableManagement vb.) "push geldi, veriyi tazele" sinyali atar
    public pushReceived = new Subject<PushNotificationSchema>();

    constructor(
        private http: HttpClient,
        private router: Router,
        private ngZone: NgZone,
        private tokenStorage: TokenStorageService,
        private userDataService: UserDataService,
        private networkErrorService: NetworkErrorService,
        private appIdentity: AppIdentityService
    ) { }

    private getAuthHeaders(): HttpHeaders {
        const token = this.tokenStorage.getAccessToken();
        return new HttpHeaders().set('Authorization', `Bearer ${token}`);
    }

    // ─────────────────────────────────────────
    // Başlatma (App başlangıcında çağrılır)
    // ─────────────────────────────────────────

    async initialize(): Promise<void> {
        if (!Capacitor.isNativePlatform()) {
            return; // Web'de push notification çalışmaz
        }

        // Push izni iste
        const pushPermission = await PushNotifications.requestPermissions();
        if (pushPermission.receive !== 'granted') {
            console.warn('Push notification izni reddedildi');
            return;
        }

        // Local notification izni iste (foreground gösterim için)
        await LocalNotifications.requestPermissions();

        // Bildirim kanalları oluştur (Android 8.0+)
        await this.createNotificationChannels();

        // Listener'ları kur (register'dan ÖNCE - cold start için)
        this.setupPushListeners();
        this.setupLocalNotificationListeners();
        this.setupAppStateListeners();

        // İnternet geri gelince tüm global_user'ı sessizce yenile
        this.networkErrorService.networkRestored$.subscribe(() => {
            this.userDataService.refreshGlobalUser().subscribe({
                next: () => {
                    this.ngZone.run(() => {
                        console.log('[NetworkRestore] global_user güncellendi, sayfalar haberdar ediliyor...');
                        this.pushReceived.next({} as PushNotificationSchema);
                    });
                },
                error: (err) => console.warn('[NetworkRestore] refreshGlobalUser başarısız:', err)
            });
        });

        // FCM'e kaydol
        await PushNotifications.register();
    }

    // ─────────────────────────────────────────
    // Notification Channels (Android 8.0+)
    // ─────────────────────────────────────────

    private async createNotificationChannels(): Promise<void> {
        try {
            // Kanal 1: Background push bildirimleri (FCM)
            // Uygulama kapalıyken/arkadayken gelen push'lar bu kanalı kullanır
            await PushNotifications.createChannel({
                id: 'high_importance_channel',
                name: 'Rezervasyon Bildirimleri',
                description: 'Yeni rezervasyon ve güncellemeler',
                importance: 5, // IMPORTANCE_HIGH = heads-up notification
                visibility: 1, // PUBLIC
                vibration: true,
                sound: 'default'
            });

            // Kanal 2: Foreground local notifications
            // Uygulama açıkken gelen push'ları ekranda göstermek için
            // (İşletim sistemi foreground'da push'u otomatik göstermez, biz LocalNotification ile zorlarız)
            await LocalNotifications.createChannel({
                id: 'foreground_channel',
                name: 'Uygulama İçi Bildirimler',
                description: 'Uygulama açıkken gelen bildirimler',
                importance: 5,
                visibility: 1,
                vibration: true,
                sound: 'default'
            });

            console.log('Notification channels created');
        } catch (error) {
            console.error('Error creating notification channels:', error);
        }
    }

    // ─────────────────────────────────────────
    // Push Notification Listeners
    // ─────────────────────────────────────────

    private setupPushListeners(): void {
        // FCM Token al ve backend'e kaydet
        PushNotifications.addListener('registration', (token: Token) => {
            console.log('FCM Token:', token.value);
            this.cachedToken = token.value;

            // Kullanıcı zaten giriş yapmışsa hemen gönder
            if (global_user.user?.id) {
                this.sendTokenToBackend(token.value);
            } else {
                console.log('User henüz login olmadı, token cache\'lendi. Login sonrası gönderilecek.');
            }
        });

        // Kayıt hatası
        PushNotifications.addListener('registrationError', (error: any) => {
            console.error('Push registration error:', error);
        });

        // ── FOREGROUND: Uygulama açıkken gelen bildirim ──
        // İşletim sistemi push'u ekranda göstermez, sadece koda verir.
        // Biz ses çalar + local notification ile gösterir + veriyi tazeleriz.
        PushNotifications.addListener('pushNotificationReceived', async (notification: PushNotificationSchema) => {
            console.log('Foreground notification:', notification);

            // Uygulama açıkken (foreground) Sagel özel sesini çal
            this.playNotificationSound();

            this.ngZone.run(() => {
                // 1. Bildirim listesini güncelle (çan ikonu + bildirim sayfası)
                this.fetchNotificationsFromBackend();

                // 2. Dinleyen sayfalara sinyal at (TableManagement sessizce veri çeker)
                this.pushReceived.next(notification);
            });

            // 3. Sistem bildirim çubuğunda göster (üstten aşağı popup)
            await this.showLocalNotification(notification);
        });

        // ── BACKGROUND/KILLED: Push bildirime tıklayınca ──
        // Uygulama uyanır, ilgili sayfaya yönlendirilir.
        // Veri zaten ngOnInit ile taze çekilir, ekstra işlem gerekmez.
        PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
            console.log('Push notification tapped:', action);
            const data = action.notification.data;
            const route = this.resolveTapRoute(data);

            // Eğer uygulama zaten açıksa (foreground/background), burası atlanır.
            if (this.router.url === '/') {
                localStorage.setItem('cold_start_notification_route', route);
            }

            // Normal foreground/background yönlendirmesi
            // (phone kanalı caller'ı doğrudan arar, diğerleri route'a gider)
            setTimeout(() => {
                this.handleNotificationTap(data);
            }, 700);
        });
    }

    // ─────────────────────────────────────────
    // Local Notification Listeners
    // ─────────────────────────────────────────

    private setupLocalNotificationListeners(): void {
        // Foreground'da gösterilen local bildirime tıklayınca
        LocalNotifications.addListener('localNotificationActionPerformed', (action) => {
            console.log('Local notification tapped:', action);
            this.handleNotificationTap(action.notification.extra);
        });
    }

    // ─────────────────────────────────────────
    // App State Listener (Uygulama Uyanma)
    // ─────────────────────────────────────────

    private setupAppStateListeners(): void {
        // Kullanıcı bildirime tıklamadan uygulamaya dönerse
        // (örn: Instagram'dan Sagel'e geçti), veriyi tazele
        App.addListener('appStateChange', ({ isActive }) => {
            if (isActive) {
                console.log('App is now active. Refreshing data...');
                this.ngZone.run(() => {
                    // TableManagement'a "veriyi sessizce yenile" sinyali at
                    this.pushReceived.next({} as PushNotificationSchema);

                    // ── BACKEND HAZIR OLUNCA: ──
                    this.fetchNotificationsFromBackend();
                });
            }
        });
    }

    // ─────────────────────────────────────────
    // Bildirim Listesi Güncelleme
    // ─────────────────────────────────────────

    private fetchNotificationsFromBackend(): void {
        const userId = global_user.user?.id;
        if (!userId) return;

        this.http.get<any>(`${environment.baseUrl}/get/notifications/${userId}`, { headers: this.getAuthHeaders() }).subscribe({
            next: (response) => {
                // Backend returns the array directly from GET /get/notifications/:userId
                const notifications = Array.isArray(response) ? response : (response.notifications || []);
                global_user.user.notifications = notifications;

                // Okunmamış bildirim sayısını yeniden hesapla (gizli sayfalar hariç)
                global_user.user.new_notifications = unreadVisibleCount(notifications, (key) =>
                    this.tokenStorage.canSeePage(key)
                );

                // Önbelleği güncelle mobil offline için
                this.userDataService.updateCachePartial({ notifications }).catch(() => {});
            },
            error: (err) => console.error('Bildirim listesi çekilemedi:', err)
        });
    }


    private playNotificationSound(): void {
        try {
            this.notificationAudio.currentTime = 0;
            this.notificationAudio.play().catch((err) =>
                console.warn('Bildirim sesi çalınamadı:', err)
            );
        } catch (e) {
            console.warn('Ses çalma hatası:', e);
        }
    }

    // ─────────────────────────────────────────
    // Local Notification Gösterimi (Foreground)
    // ─────────────────────────────────────────

    private async showLocalNotification(pushNotification: PushNotificationSchema): Promise<void> {
        try {
            const notificationId = Math.floor(Math.random() * 100000);

            await LocalNotifications.schedule({
                notifications: [{
                    id: notificationId,
                    title: pushNotification.title || 'Bildirim',
                    body: pushNotification.body || '',
                    channelId: 'foreground_channel',
                    extra: pushNotification.data, // Tıklayınca route bilgisi için
                    smallIcon: "ic_notification",
                    iconColor: "#162746"
                }]
            });
            console.log('Local notification shown, id:', notificationId);
        } catch (error) {
            console.error('Error showing local notification:', error);
        }
    }

    // ─────────────────────────────────────────
    // Yardımcı Metodlar
    // ─────────────────────────────────────────

    // Bildirime tıklanınca yönlendirme kararı: telefon bota gelen "insana aktar"
    // bildirimi ise (channel === 'phone') doğrudan arayanı çevirir, aksi halde
    // eskiden olduğu gibi ilgili sayfaya gider.
    private handleNotificationTap(data: any): void {
        const channel = data?.['channel'];
        const caller = data?.['caller'];
        if (channel === 'phone' && caller) {
            this.callNumber(caller);
            return;
        }
        this.navigateTo(this.resolveTapRoute(data));
    }

    // Where a tapped notification should land. A bare backend `route` wins, but
    // when it is missing (or just the generic notifications list) we try to
    // build a deep link from the payload: a reservation id opens that
    // reservation on the calendar; a messaging channel + sender id opens that
    // conversation on the matching bot page. Otherwise fall back to the list.
    private resolveTapRoute(data: any): string {
        const explicitRoute = data?.['route'];

        // A specific backend route always wins.
        if (explicitRoute && explicitRoute !== '/pages/notifications') {
            return explicitRoute;
        }

        // A messaging handoff ("insana aktar") is about a conversation, never a
        // reservation — resolve it first so a reservation id that happens to
        // ride along in the same payload can't hijack the tap to the calendar.
        const source = String(
            data?.['source'] ?? data?.['channel'] ?? ''
        ).trim().toLowerCase();
        const messagingPage: Record<string, string> = {
            email: '/pages/email_bot',
            whatsapp: '/pages/whatsapp_bot',
            telegram: '/pages/telegram_bot',
        };
        if (messagingPage[source]) {
            return this.buildConversationRoute(data) ?? messagingPage[source];
        }

        // Otherwise, a reservation id opens that reservation. This service only
        // runs on the native app, whose calendar page isn't mobile-friendly —
        // route to the dashboard, which opens the same detail modal in the
        // mobile layout.
        const reservationId =
            data?.['reservation_id'] ??
            data?.['reservationId'] ??
            data?.['reservation'];
        if (reservationId) {
            return `/dashboard?reservation_id=${encodeURIComponent(
                String(reservationId)
            )}`;
        }

        return explicitRoute || '/pages/notifications';
    }

    // "Insana aktar" (human handoff) pushes carry the messaging channel and the
    // sender/thread id — turn those into the same bot-page deep link the
    // reservation detail's "Konuşmaya Git" uses, so the tap opens the actual
    // conversation instead of the notifications list. Phone handoff is handled
    // earlier (it dials the caller), so it is intentionally not covered here.
    private buildConversationRoute(data: any): string | null {
        const source = String(
            data?.['source'] ?? data?.['channel'] ?? ''
        ).trim().toLowerCase();
        const sourceId =
            data?.['source_id'] ??
            data?.['sourceId'] ??
            data?.['thread_id'] ??
            data?.['threadId'] ??
            data?.['from'];
        if (!source || !sourceId) {
            return null;
        }

        const routeBySource: Record<string, string> = {
            email: '/pages/email_bot',
            whatsapp: '/pages/whatsapp_bot',
            telegram: '/pages/telegram_bot',
        };
        const page = routeBySource[source];
        if (!page) {
            return null;
        }

        const params = new URLSearchParams({
            source,
            source_id: String(sourceId),
        });
        const confirmationDate = data?.['confirmation_date'];
        if (confirmationDate) {
            params.set('confirmation_date', String(confirmationDate));
        }
        return `${page}?${params.toString()}`;
    }

    // Telefon çeviriciyi arayanın numarasıyla açar (tel: şeması). Numara yoksa
    // bildirimler sayfasına düşer.
    private callNumber(rawNumber: string): void {
        const number = String(rawNumber || '').replace(/[^\d+]/g, '');
        if (!number) {
            this.navigateTo('/pages/notifications');
            return;
        }
        try {
            // Capacitor'da '_system' hedefi tel: şemasını cihazın çeviricisine verir.
            window.open(`tel:${number}`, '_system');
        } catch {
            window.location.href = `tel:${number}`;
        }
    }

    private navigateTo(path: string): void {
        this.ngZone.run(() => {
            this.router.navigateByUrl(path);
        });
    }

    // Login sonrası çağrılır — cache'lenmiş FCM token'ı backend'e gönderir
    public async registerCachedToken(): Promise<void> {
        console.log('registerCachedToken çağrıldı. cachedToken:', !!this.cachedToken, 'global_user.id:', global_user.user?.id);

        if (this.cachedToken && global_user.user?.id) {
            console.log('Login sonrası cached token gönderiliyor... userId:', global_user.user.id);
            await this.sendTokenToBackend(this.cachedToken);
        } else if (!this.cachedToken) {
            console.log('Henüz FCM token alınmadı, gönderilecek bir şey yok.');
        } else {
            console.warn('FCM token var ama userId bulunamadı! Token gönderilemedi.');
        }
    }

    private async sendTokenToBackend(token: string): Promise<void> {
        // Garson ve mutfak oturumları cihaz kaydetmez: kayıt kiracı geneline
        // gider ve sahibe giden her bildirimi bu cihaza da getirirdi. Kişiye
        // özel kayıt sonraki aşamada gelecek.
        if (this.tokenStorage.isMarsOnly()) {
            return;
        }

        // Hangi kabuk çalışıyor: com.demoemre.app mı, com.sagel.app mı. Token
        // bu değere göre ilgili Firebase projesinin container'ına yazılır (V109).
        //
        // Kimlik bilinmiyorsa KAYIT YAPILMAZ. appId'siz istek backend'de eski
        // uygulama sayılır; yeni uygulamanın token'ı eski projeye yazılır ve o
        // cihaza bildirim bir daha hiç ulaşmaz — hata da vermez, gönderim
        // döngüsü token başına sessizce başarısız olur. Atlanan kayıt bir
        // sonraki login veya uygulama açılışında yeniden denenir.
        const appId = await this.appIdentity.getAppId();
        if (!appId) {
            console.error('FCM token gönderilmedi: uygulama kimliği (appId) alınamadı.');
            return;
        }

        const url = `${environment.baseUrl}/post/devices/register`;
        console.log('FCM token gönderiliyor. URL:', url);
        console.log('User ID:', global_user.user?.id, 'Platform:', Capacitor.getPlatform(), 'App:', appId);

        this.http.post(url, {
            token,
            platform: Capacitor.getPlatform(),
            userId: global_user.user?.id,
            appId
        }, { headers: this.getAuthHeaders() }).subscribe({
            next: (response) => console.log('FCM token backend\'e kaydedildi. Response:', JSON.stringify(response)),
            error: (err) => {
                console.error('FCM token kayıt hatası:');
                console.error('  Status:', err.status);
                console.error('  StatusText:', err.statusText);
                console.error('  URL:', err.url);
                console.error('  Message:', err.message);
                console.error('  Error body:', JSON.stringify(err.error));
            }
        });
    }
}
